import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import { alpha, Box, CircularProgress, IconButton, Stack, styled, Typography } from '@mui/material';
import { X } from 'lucide-react';
import { SnackbarContent } from 'notistack';
import { forwardRef, HTMLAttributes, ReactNode } from 'react';

export type VideoGenerationToastVariant = 'generating' | 'ready' | 'failed';

export interface VideoGenerationToastProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title' | 'children'> {
  variant: VideoGenerationToastVariant;
  title: string;
  description: string;
  closeLabel: string;
  onClose: () => void;
  action?: ReactNode;
}

const TILE_COLOR: Record<VideoGenerationToastVariant, 'primary' | 'success' | 'warning'> = {
  generating: 'primary',
  ready: 'success',
  failed: 'warning',
};

const TILE_ICON: Record<VideoGenerationToastVariant, ReactNode> = {
  generating: <CircularProgress size={28} thickness={4} color="primary" />,
  ready: <CheckCircleOutline color="success" sx={{ fontSize: 32 }} />,
  failed: <ErrorOutline color="warning" sx={{ fontSize: 32 }} />,
};

// notistack's own styles target `.SnackbarContent-root`, so the card styles repeat that class to win on specificity.
const ToastRoot = styled(SnackbarContent)(({ theme }) => ({
  '&.SnackbarContent-root': {
    flexWrap: 'nowrap',
    maxWidth: '25rem',
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.text.primary,
    boxShadow: theme.shadows[6],
  },
}));

/**
 * Toast body for video generation (generating, ready and failed), rendered through notistack's `content` option.
 *
 * It renders above `LocalizedIntlProvider`, so it takes translated strings and never calls intl hooks. The ref and the
 * remaining props (e.g. `style`) reach the root, which notistack's transitions need to animate it.
 */
export const VideoGenerationToast = forwardRef<HTMLDivElement, VideoGenerationToastProps>(
  ({ variant, title, description, closeLabel, onClose, action, ...rest }, ref) => (
    <ToastRoot ref={ref} role="alert" {...rest}>
      <Stack direction="row" alignItems="center" columnGap={2} sx={{ flex: 1, minWidth: 0 }}>
        <Box
          aria-hidden
          sx={{
            width: 56,
            height: 56,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 1,
            backgroundColor: ({ palette }) => alpha(palette[TILE_COLOR[variant]].main, 0.12),
          }}
        >
          {TILE_ICON[variant]}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </Box>
        {action ? <Box sx={{ flexShrink: 0 }}>{action}</Box> : null}
      </Stack>
      <IconButton
        size="small"
        aria-label={closeLabel}
        onClick={onClose}
        sx={{ alignSelf: 'flex-start', ml: 1, mt: -0.5, mr: -0.5 }}
      >
        <X size={16} />
      </IconButton>
    </ToastRoot>
  ),
);

VideoGenerationToast.displayName = 'VideoGenerationToast';
